import Chart from './Chart'
import { IGridInfo, ILineChartAxisSettings, ILineChartClassConstructor } from './lineChart.types'
import { ILineChartSettings } from '../lineChart.types'
import { TDataRef } from '@/services/data/data.types.ts'
import { RefObject } from 'react'
import { IChartBoundaries } from './Chart/chart.types'
import lineChartCoreUtils from './lineChart.core.utils'
import LineChartCursor from './LineChartCursor'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'

class CLineChart {
  public readonly chart: Chart

  public config: ILineChartAxisSettings
  private keepLastValue = false
  private minY: number[]
  private maxY: number[]

  public innerHeight: number
  public yScales: Array<(val: number) => number>
  public yScalesInverse: Array<(val: number) => number>
  private yScaleFactors: number[]
  private lineLastValues: Record<IEquipment['id'], Record<IParameter['id'], number>> = {}

  private xGrid = { start: 0, step: 0 }
  private yGrid: IGridInfo
  public margins = { top: 10, right: 50, bottom: 25, left: 50 }

  // Коэффициент для проекции времени на координату
  private xScaleFactor: number
  // Ширина графика за вычетом отступов
  public innerWidth: number

  constructor(config: ILineChartClassConstructor) {
    this.chart = new Chart(config)
    this.chart.addExecutable(this.render)
    this.config = {
      title: config.config.title,
      axes: [{ lines: config.config.lines, position: 'left' }],
      position: 'left',
      lineMode: config.config.lineMode,
      minY: config.config.minY,
      maxY: config.config.maxY,
      removeExtreme: config.config.removeExtreme,
    }

    this.keepLastValue = config.keepLastValue

    this.updateBoundaries(config.boundaries)
    this.computeXScaleFactor()

    new LineChartCursor(config.tooltipRef, this, config.parametersMap, config.unitsMap, config.unitPrefixesMap)
  }

  public updateConf(updated: ILineChartSettings) {
    this.config = {
      title: updated.title,
      axes: [{ lines: updated.lines, position: 'left' }],
      position: 'left',
      lineMode: updated.lineMode,
      minY: updated.minY,
      maxY: updated.maxY,
      removeExtreme: updated.removeExtreme,
    }
  }

  public updateFrame(frame: number) {
    this.chart.updateFrame(frame)
    this.computeXScaleFactor()
  }

  private computeXScaleFactor() {
    this.xScaleFactor = this.innerWidth / this.chart.frame
  }

  public setIsLive(live: boolean) {
    this.chart.setLive(live)
  }

  public updateTimeInterval = (tsFrom: number, tsTo: number) => {
    this.chart.updateTimeInterval(tsFrom, tsTo)
  }

  public updateBoundaries = (updated: IChartBoundaries) => {
    this.chart.updateBoundaries(updated)
    this.innerWidth = this.chart.boundaries.width - this.margins.left - this.margins.right
    this.innerHeight = this.chart.boundaries.height - this.margins.top - this.margins.bottom

    this.computeMinMaxY(this.chart.dataRef.current)
    this.computeXScaleFactor()
  }

  private render = (
    ctx: CanvasRenderingContext2D,
    data: TDataRef,
    __: RefObject<HTMLDivElement>,
    t0: number,
    t1: number
  ) => {
    ctx.font = 'Inter 12px'
    this.clearTails(ctx)

    this.computeMinMaxY(data)
    this.drawData(ctx, t0)
    this.cleatOffset(ctx)
    // this.drawThresholds(ctx)

    // отрисовка линий сетки
    ctx.strokeStyle = 'rgba(47, 43, 67, 0.3)'
    ctx.lineWidth = 1
    this.drawXAxis(ctx, t0, t1)
    this.drawXAxisText(ctx, t0, t1)
    this.drawYAxis(ctx)
    this.drawYAxisText(ctx)
  }

  // зная метку времени рассчитать ее координату по оси X (в пикселях)
  public xScale = (ts: number, t0: number) => {
    return this.margins.left + (ts - t0) * this.xScaleFactor
  }

  private drawXAxis(ctx: CanvasRenderingContext2D, t0: number, t1: number) {
    const { boundaries } = this.chart
    const y0 = this.margins.top
    const y1 = boundaries.height - this.margins.bottom

    const len = 5
    this.xGrid = lineChartCoreUtils.calcTimeStep(t1 - t0, t0, len)

    const { start, step } = this.xGrid

    ctx.beginPath()
    ctx.moveTo(this.margins.left + 0.5, y0)
    ctx.lineTo(this.margins.left + 0.5, y1)
    ctx.moveTo(boundaries.width - this.margins.right - 0.5, y0)
    ctx.lineTo(boundaries.width - this.margins.right - 0.5, y1)

    for (let i = 0; i < len; i++) {
      const ts = start + i * step
      if (ts > t1) continue

      const x = Math.round(this.xScale(ts, t0)) + 0.5

      ctx.moveTo(x, y0)
      ctx.lineTo(x, y1)
    }

    ctx.stroke()
    ctx.closePath()
  }

  // отрисовка подписей к линиям сетки по оси времени
  private drawXAxisText(ctx: CanvasRenderingContext2D, t0: number, t1: number) {
    const { start, step } = this.xGrid

    const { height, width } = this.chart.boundaries
    const { bottom, right } = this.margins
    const y = height - bottom + 18
    const frame = t1 - t0

    ctx.textBaseline = 'alphabetic'
    ctx.textAlign = 'center'

    for (let i = 0; i < 5; i++) {
      const ts = start + i * step
      if (ts > t1) break

      const x = Math.round(this.xScale(ts, t0)) + 0.5
      ctx.fillText(lineChartCoreUtils.formatTime(ts, frame), x, y)
    }

    // подсказка с форматом выводимого времени в правом нижнем углу
    ctx.textAlign = 'right'
    ctx.clearRect(width - right, height - bottom, right, bottom)
    const help = lineChartCoreUtils.formatTimeHelp(frame)
    ctx.fillText(help, width - 2, y)
  }

  // отрисовка линий сетки по оси Y
  private drawYAxis(ctx: CanvasRenderingContext2D) {
    const { boundaries } = this.chart
    const x0 = this.margins.left
    const x1 = boundaries.width - this.margins.right
    const yScale = this.yScales[0]

    ctx.beginPath()
    ctx.moveTo(x0, this.margins.top + 0.5)
    ctx.lineTo(x1, this.margins.top + 0.5)
    ctx.moveTo(x0, boundaries.height - this.margins.bottom - 0.5)
    ctx.lineTo(x1, boundaries.height - this.margins.bottom - 0.5)

    for (const value of this.yGrid.values) {
      const y = Math.round(yScale(value)) + 0.5
      ctx.moveTo(x0, y)
      ctx.lineTo(x1, y)
    }

    ctx.stroke()

    ctx.closePath()
  }

  // отрисовка подписей к линиям сетки по оси Y
  private drawYAxisText(ctx: CanvasRenderingContext2D) {
    const { boundaries } = this.chart
    const settings = this.config

    ctx.textBaseline = 'middle'
    ctx.strokeStyle = 'var(--black-10)'
    ctx.lineWidth = 1

    // количество осей слева и справа, которые уже были отрисованы
    const positions = { left: 0, right: 0 }
    const width = 50

    // подписи по Y делаем для каждой из осей графика
    for (let i = 0; i < settings.axes.length; i++) {
      const position = settings.axes[i].position
      positions[position] += 1

      const isRight = position === 'right'

      ctx.textAlign = isRight ? 'start' : 'end'
      const x = isRight ? boundaries.width - positions.right * width : positions.left * width
      const shift = isRight ? 5 : -5

      const scale = 1

      const axisStep = (this.maxY[i] - this.minY[i]) / this.yGrid.values.length
      const stepPrecision = lineChartCoreUtils.getNumberPrecision(axisStep / scale)

      ctx.beginPath()
      ctx.moveTo(x, this.margins.top)
      ctx.lineTo(x, boundaries.height - this.margins.bottom)

      for (let value of this.yGrid.values) {
        const y = Math.round(this.yScales[0](value)) + 0.5
        if (y < 50 || y > boundaries.height - 25) continue

        // небольшой прочерк на оси рядом с подписью
        ctx.moveTo(x - 3, y)
        ctx.lineTo(x + 3, y)

        value /= scale

        const text = value.toFixed(stepPrecision)

        const textX = text.length < 8 ? x + shift : x // при слишком длинной подписи сдвигаем ее к самой оси
        ctx.fillText(text, textX, y, width)
      }

      ctx.stroke()
    }
  }

  private computeMinMaxY = (dataRef: TDataRef) => {
    this.maxY = []
    this.minY = []

    for (const axe of this.config.axes) {
      let axeMax = -Number.MAX_SAFE_INTEGER
      let axeMin = Number.MAX_SAFE_INTEGER

      for (const line of axe.lines) {
        let lineMax = -Number.MAX_SAFE_INTEGER
        let lineMin = Number.MAX_SAFE_INTEGER

        if (!Array.isArray(dataRef[line.deviceId])) continue

        for (const point of dataRef[line.deviceId]) {
          const value = point.values.find((v) => v.parameterId === line.parameterId)

          if (value && !Number.isNaN(value.value)) {
            lineMax = Math.max(lineMax, value.value)
            lineMin = Math.min(lineMin, value.value)
          }
        }

        axeMax = Math.max(axeMax, lineMax)
        axeMin = Math.min(axeMin, lineMin)
      }

      if (this.config.maxY) {
        axeMax = this.config.maxY
      }
      if (this.config.minY) {
        axeMin = this.config.minY
      }

      if (axeMax === 0 && axeMin === 0) {
        this.maxY.push(0.2)
        this.minY.push(-0.2)
      } else {
        this.maxY.push(axeMax * 1.1)
        this.minY.push(axeMin - axeMax * 0.1)
      }
    }

    this.yScaleFactors = this.maxY.map((_, i) => this.innerHeight / (this.maxY[i] - this.minY[i]))
    this.yScales = this.maxY.map((_, i) => {
      return (value: number) => this.margins.top + (this.maxY[i] - value) * this.yScaleFactors[i]
    })
    this.yScalesInverse = this.maxY.map((_, i) => {
      return (y: number) => this.maxY[i] - (y - this.margins.top) / this.yScaleFactors[i]
    })
    this.yGrid = lineChartCoreUtils.calcYGrid(this.minY[0], this.maxY[0], this.chart.boundaries.height / 50 || 1)
  }

  // отрисовка линий пороговых значений
  /* private drawThresholds(ctx: CanvasRenderingContext2D) {
    ctx.beginPath()
    ctx.lineWidth = 2

    const { axes } = this.config
    const { left, right } = this.margins
    const { minY, maxY } = this

    for (let i = 0; i < axes.length; i++) {
      const axis = axes[i]
      const { thresholds } = axis
      if (!thresholds || thresholds.length === 0) continue

      for (const t of thresholds) {
        if (t.value > minY[i] && t.value < maxY[i]) {
          const y = Math.round(this.yScales[i](t.value))
          ctx.strokeStyle = t.color
          ctx.setLineDash(t.line === 'solid' ? [] : [6, 8])
          ctx.beginPath()
          ctx.moveTo(left, y)
          ctx.lineTo(this.chart.boundaries.width - right, y)
          ctx.stroke()
        }
      }
    }

    ctx.setLineDash([])
    ctx.closePath()
  }*/

  private drawData = (ctx: CanvasRenderingContext2D, t0: number) => {
    let axeIndex = 0

    for (const axe of this.config.axes) {
      for (const line of axe.lines) {
        const { deviceId, parameterId, color } = line
        if (!Array.isArray(this.chart.dataRef.current[deviceId])) continue

        const points: Array<{ ts: number; value: number }> = this.chart.dataRef.current[deviceId].map((p) => ({
          ts: p.ts,
          value: p.values.find((v) => v.parameterId === parameterId)?.value,
        }))

        if (this.keepLastValue && points.length) {
          const now = Date.now()
          if (now - points[points.length - 1].ts > 200) {
            points.push({ ...points[points.length - 1], ts: Date.now() - 100 })
          }

          if (!this.lineLastValues[deviceId]) {
            this.lineLastValues[deviceId] = {}
          }
          this.lineLastValues[deviceId][parameterId] = points[points.length - 1].value
        }

        if (this.keepLastValue && !points.length) {
          const lastValue = this.lineLastValues?.[deviceId]?.[parameterId] || 0
          points.push({ ts: Date.now() - this.chart.frame, value: lastValue })
          points.push({ ts: Date.now(), value: lastValue })
        }

        if (this.config.lineMode === 'spline') {
          lineChartCoreUtils.drawCurvedLine({
            data: points,
            ctx,
            yScale: this.yScales[axeIndex],
            xScale: (ts) => this.xScale(ts, t0),
            disconnectDistance: this.keepLastValue ? Number.MAX_SAFE_INTEGER : 100,
            color,
            minValue: this.minY[axeIndex] || Number.MIN_VALUE,
            maxValue: this.maxY[axeIndex] || Number.MAX_VALUE,
          })
        } else {
          lineChartCoreUtils.drawLine({
            data: points,
            ctx,
            mode: (this.config.lineMode as any) || 'step',
            yScale: this.yScales[axeIndex],
            xScale: (ts) => this.xScale(ts, t0),
            disconnectDistance: this.keepLastValue ? Number.MAX_SAFE_INTEGER : 100,
            color,
            removeExtreme: this.config.removeExtreme,
          })
        }
      }

      axeIndex++
    }
  }

  private clearTails = (ctx: CanvasRenderingContext2D) => {
    const { width, height } = this.chart.boundaries
    ctx.clearRect(0, 0, width, height)
  }

  private cleatOffset = (ctx: CanvasRenderingContext2D) => {
    const { height, width } = this.chart.boundaries
    ctx.clearRect(0, 0, this.margins.left, height)
    ctx.clearRect(width - this.margins.right, 0, width, height)
  }
}

export default CLineChart
